import { useEffect, useRef } from 'react'
import useCrashStore from '../store/useCrashStore'
import { PHASES, TICK_TIMEOUT_MS, OFFLINE_NOTICE_MS } from '../config/crashConfig'
import { calcMultiplierHundreds } from '../utils/crashHelpers'

/**
 * useCrashMultiplier — отрисовка множителя в requestAnimationFrame + watchdog.
 *
 * Правило (§6, §8.1 краш-документации):
 * - множитель считается клиентом по формуле Math.exp(growth * elapsedSec)
 *   от startTime с поправкой на часы сервера (clockOffset);
 * - тики (crash:tick) НЕ источник значения — только коррекция;
 * - НО расти без тиков нельзя: если тиков нет дольше 500мс — замораживаем на
 *   последнем присланном тике (lastTickMult), переводим кривую в «stale» состояние;
 * - дольше 3000мс — показываем плашку «связь потеряна»;
 * - watchdog активен ТОЛЬКО в фазе running. В betting/busted тиков нет по определению.
 *
 * RAF запускается один раз и фильтрует логику по phase, читая состояние из store.
 */
export const useCrashMultiplier = () => {
  const rafRef = useRef(null)

  useEffect(() => {
    const frame = () => {
      const state = useCrashStore.getState()

      if (state.phase === PHASES.RUNNING && state.startTime && state.growth) {
        const silence = Date.now() - state.lastTickAt
        const isStale = silence > TICK_TIMEOUT_MS
        const showOfflineNotice = silence > OFFLINE_NOTICE_MS

        let multiplierHundreds
        if (isStale) {
          // Заморозка на последнем присланном тике — оно реально существовало на сервере.
          // Своё вычисленное в этот момент — уже гипотеза (§8.1).
          multiplierHundreds = state.lastTickMult
        } else {
          multiplierHundreds = calcMultiplierHundreds(
            state.startTime,
            state.growth,
            state.clockOffset
          )
        }

        // Пишем в store только если изменилось (или сменилось stale-состояние)
        if (
          multiplierHundreds !== state.multiplierHundreds ||
          isStale !== state.isStale ||
          showOfflineNotice !== state.showOfflineNotice
        ) {
          state.setMultiplier(multiplierHundreds, isStale, showOfflineNotice)
        }
      }

      rafRef.current = requestAnimationFrame(frame)
    }

    rafRef.current = requestAnimationFrame(frame)

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [])
}

export default useCrashMultiplier
